import { useState, useContext, useMemo } from 'react';
import liCSS from 'components/Shared/MakeModelMenu/liCSS';
import MakeModelHeader from 'components/Shared/MakeModelMenu/MakeModelHeader';
import { useTranslation } from 'react-i18next';
import oemLogosSprite from 'public/static/images/oem-logos-sprite.png';
import selectedCheckIcon from 'public/static/images/srp/selectedCheck.svg';

import Make from 'types/Make';
import logoSpritePositions from 'components/styles/logoSpritePositions';
import useEvents from 'lib/hooks/useEvents';
import { MakeTap } from 'lib/Tracking/MakeModelMenu/MakeModelMenuEvents';
import { FilterEventContext } from 'contexts/FilterEventContext';
import { useMultiMakeModelFilter } from 'lib/hooks/useSrpFilters';
import { ChooseFunction } from './types';

interface Props {
  makes: Make[];
  allMakesAndModelsOption?: boolean;
  choose: ChooseFunction;
  chooseAll: ChooseFunction;
  className?: string;
}

export default function MakesList({
  makes,
  allMakesAndModelsOption = true,
  choose,
  chooseAll,
  className,
}: Props): JSX.Element {
  const [filter, setFilter] = useState('');
  const { t } = useTranslation();
  const logger = useEvents();
  const filterEventContext = useContext(FilterEventContext);
  const { value } = useMultiMakeModelFilter();

  const selectedMakes = useMemo(() => {
    if (!value) return [];

    return value.reduce((uniqueMakes: string[], item) => {
      if (item && item.make && !uniqueMakes.includes(item.make)) {
        uniqueMakes.push(item.make);
      }
      return uniqueMakes;
    }, []);
  }, [value]);

  function filteredMakes(): Make[] {
    if (!filter) {
      return makes;
    }

    return makes.filter(({ name }) => name.toLowerCase().includes(filter.toLowerCase()));
  }

  return (
    <ul data-makes-list className={`${className} make-model-menu-makes-list`}>
      <MakeModelHeader
        includeAll={allMakesAndModelsOption}
        chooseAll={chooseAll}
        handleFilter={setFilter}
        allText={t('makeModelSearch.allMakes')}
        makeOrModel={'make'}
      />
      {filteredMakes().map((make) => (
        <li
          className="make-model-menu-makes-list-item"
          key={make.name}
          data-make={make.name}
          onClick={(event): void => {
            event.stopPropagation();
            logger.log(MakeTap(filterEventContext));
            choose(make);
          }}
        >
          <div className="logo-container">
            <div className={`logo logo--${make.name}`} />
          </div>
          <div className="name">{make.name}</div>
          {selectedMakes?.includes(make.name) && <div className="selected-icon" />}
        </li>
      ))}
      <style jsx>{liCSS}</style>

      <style jsx>
        {`
          .make-model-menu-makes-list {
            margin: 0;
            padding: 0;
          }

          .make-model-menu-makes-list-item {
            display: flex;
            flex-direction: row;
            align-items: center;
            overflow: hidden;
            border-bottom: none;
          }

          .name {
            font-size: 14px;
            margin-left: 3px;
          }

          .logo-container {
            width: 48px;
            height: 48px;
          }

          .logo {
            background-image: url('${oemLogosSprite}');
            background-repeat: no-repeat;
            background-size: 80%;
            height: 60px;
            width: 60px;
          }

          .selected-icon {
            background-image: url(${selectedCheckIcon});
            width: 17px;
            height: 17px;
            background-size: contain;
            background-repeat: no-repeat;
            position: absolute;
            right: 14px;
            bottom: 16px;
          }
        `}
      </style>
      <style jsx>{logoSpritePositions}</style>
    </ul>
  );
}
